import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { visuallyHidden } from "@mui/utils";
import { Header } from "Components/Header";
import { LocalImage } from "Utils/images";
import { Colors } from "Utils/colors";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Grid,
  Snackbar,
  Stack,
} from "@mui/material";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  deleteUser,
  listUser,
  resetCustomerPassword,
} from "Features/slice/userSlice";
import { useAppDispatch, useAppSelector } from "Hooks/reduxHook";
import { useCallback, useContext, useEffect, useState } from "react";
import { RootState } from "Features/store";
import CancelIcon from "@mui/icons-material/Cancel";
import AddUser from "./AddUser";
import { UserInterface, UserListInterface } from "Features/slice/user.model";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { MenuItem, Select } from "@mui/material";
import { ResetPassword } from "./ResetPassword";
import { firstLetterCaps, formatAsUsPhoneNo } from "Utils/transform";
import { isValidString } from "Utils/validators";
import { SnackBarContext } from "Context/SnackContext";
import LockResetIcon from "@mui/icons-material/LockReset";

type TabInterface = "all" | "active" | "inactive";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function getListByTab(
  list: UserListInterface[],
  tab: TabInterface
): UserListInterface[] {
  return list?.filter((item: UserListInterface) =>
    tab === "active"
      ? item.is_active === 1
      : tab === "inactive"
      ? item.is_active === 0
      : item
  );
}

function stableSort<T>(
  array: readonly UserListInterface[],
  comparator: (a: T, b: T) => number
) {
  if (array && array?.length > 0) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number]
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }
  return [];
}

interface Data extends UserListInterface {
  action: string;
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "user_id",
    numeric: false,
    disablePadding: false,
    label: "Customer ID",
  },
  {
    id: "username",
    numeric: false,
    disablePadding: false,
    label: "Username",
  },
  {
    id: "email",
    numeric: false,
    disablePadding: false,
    label: "Email",
  },
  {
    id: "phone",
    numeric: true,
    disablePadding: false,
    label: "Phone",
  },
  {
    id: "first_name",
    numeric: false,
    disablePadding: false,
    label: "First Name",
  },
  {
    id: "last_name",
    numeric: false,
    disablePadding: false,
    label: "Last Name",
  },
  {
    id: "is_active",
    numeric: false,
    disablePadding: false,
    label: "Active",
  },
  {
    id: "role_name",
    numeric: false,
    disablePadding: false,
    label: "Role",
  },
  {
    id: "action",
    numeric: false,
    disablePadding: false,
    label: "Update",
  },
  {
    id: "action",
    numeric: false,
    disablePadding: false,
    label: "Delete",
  },
  {
    id: "action",
    numeric: false,
    disablePadding: false,
    label: "Reset Password",
  },
];

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, rowCount, onRequestSort } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead sx={{ background: "#007CA40D" }}>
      <TableRow sx={{ borderRight: "none", borderLeft: "none" }}>
        {headCells?.map((headCell) => (
          <TableCell
            sx={{
              background: "#007CA40D",
              borderBottom: "none",
              textAlign: "left",
              borderRight: "none",
              borderLeft: "none",
            }}
            key={headCell.id}
            // align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface IEditForm {
  user_id: number;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  phone: string;
  role_name: string;
  isEditForm: boolean;
  password: string;
  is_active: number;
}

export default function Users() {

  const dispatch = useAppDispatch();
  const { handleSnackbar } = useContext(SnackBarContext);

  const {
    listUser: usersList,
    userListFetchError,
    userListFetching,
  } = useAppSelector((state: RootState) => state.user);

  const rowsPerPage = 10;

  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof Data>("user_id");
  const [dense, setDense] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IEditForm>({} as IEditForm);
  const [rows, setRows] = useState<UserListInterface[]>(usersList);
  const [selectedTab, setSelectedTab] = useState<TabInterface>("all");
  const [pageNo, setPageNo] = useState(1);
  const [resetPwdModal, setResetPwdModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [deleteConfirmAlert, setDeleteConfirmAlert] = useState({
    visible: false,
    id: "",
  });

  const paginationOrder = Math.ceil(rows?.length / 10) || 0;

  useEffect(() => {
    getUsersList();
  }, []);

  useEffect(() => {
    onClickTab(selectedTab);
    setRows(usersList);
  }, [usersList]);

  const getUsersList = () => {
    dispatch(listUser());
  };

  const onClickTab = (tab: TabInterface) => {
    setPageNo(1);
    // const filteredList = usersList?.filter((item: UserInterface) =>
    //   tab === 'active' ? item.is_active === 1 :
    //     tab === 'inactive' ? item.is_active === 0 : item);
    // setRows(filteredList);
    setSelectedTab(tab);
  };

  const openAddUserModal = (mode: "edit" | "add" = "add", data?: Data) => {
    setSelectedUser({} as IEditForm);
    setAddModal(true);
    if (mode == "edit") {
      const phone = isValidString(data?.phone)
        ? data?.phone?.replace(/[()-]/g, "")
        : data?.phone;
      const payload = {
        user_id: data?.user_id,
        first_name: data?.first_name,
        last_name: data?.last_name,
        username: data?.username,
        email: data?.email,
        phone,
        role_name: data?.role_name,
        isEditForm: true,
        password: "",
        is_active: data?.is_active as number,
      };
      setSelectedUser(payload as IEditForm);
    }
  };

  const closeAddUserModal = () => {
    setAddModal(false);
    getUsersList();
  };

  const addNewUser = (response: boolean, message: string = "") => {
    if (isValidString(message)) {
      handleSnackbar(true, message, response ? "success" : "error");
    }
    if (response) {
      getUsersList();
      closeAddUserModal();
    }
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const visibleRows = React.useMemo(
    () =>
      stableSort(
        getListByTab(rows, selectedTab),
        getComparator(order, orderBy)
      )?.slice(
        (pageNo - 1) * rowsPerPage,
        (pageNo - 1) * rowsPerPage + rowsPerPage
      ),
    [rows, order, orderBy, pageNo, rowsPerPage, selectedTab]
  );

  const changePageNo = (no: number) => {
    setPageNo(no);
  };

  const getCurrentItemsCount = () => {
    
    if ((pageNo * 10) % visibleRows?.length === 0) {
      if (visibleRows?.length % 10 === 0) {
        return pageNo * 10;
      }
      return rows?.length;
    }
    return visibleRows?.length;
  };

  const handleDeleteConfirmAlert = (visible: boolean, id: string = "") => {
    setDeleteConfirmAlert({ visible, id });
  };

  const deleteUserById = (userId: string) => {
    handleDeleteConfirmAlert(true, userId);
  };

  const proceedToDelete = (userId: string) => {
    handleDeleteConfirmAlert(false, "");
    setIsLoading(true);
    dispatch(
      deleteUser({
        userId,
        successCB: (response: any) => {
          setIsLoading(false);
          getUsersList();
          handleSnackbar(true, "Account Deleted Successfully!", "success");
        },
        failureCB: (error: any) => {
          setIsLoading(false);
          const message =
            error?.response?.data?.message || "Something went wrong";
          handleSnackbar(true, message, "error");
        },
      })
    );
  };

  const searchByField = (value: string, input: string) => {
    return value.startsWith(input.toLowerCase());
  };

  const handleSearch = useCallback(
    (value: string) => {
      if (isValidString(value)) {
        const filteredList = usersList.filter(
          (item) =>
            searchByField(item.first_name.toLowerCase(), value) ||
            searchByField(item.last_name.toLowerCase(), value) ||
            searchByField(item.username.toLowerCase(), value)
        );
        setRows(filteredList || []);
      } else {
        setRows(usersList);
      }
    },
    [rows]
  );

  const defaultPasswordResetValues = {
    alert: {
      user_id: "",
      visible: false,
      title: "Are you sure to reset the password of this user?",
      desc: "",
    },
    success: {
      password: "",
      visible: false,
      title: "Password for this user has been resetted successfully!",
      desc: "You can copy the below password and share it with user",
    },
  };
  const [passwordReset, setPasswordReset] = useState(
    defaultPasswordResetValues
  );

  const resetUserPassword = () => {
    setIsLoading(true);
    const payload = {
      user_id: passwordReset.alert.user_id,
    };
    dispatch(
      resetCustomerPassword({
        payload,
        successCB: (response: any) => {
          setIsLoading(false);
          setPasswordReset({
            ...passwordReset,
            success: {
              ...passwordReset.success,
              visible: true,
              password: response?.updatedPassword,
            },
          });
        },
        failureCB: (error: any) => {
          setIsLoading(false);
          const message =
            error?.response?.data?.message || "Something went wrong";
          handleSnackbar(true, message, "error");
        },
      })
    );
  };

  const handleResetPassword = (user_id: number) => {
    setPasswordReset({
      ...passwordReset,
      alert: {
        ...passwordReset.alert,
        user_id: user_id?.toString(),
        visible: true,
      },
    });
  };

  const closePasswordResetModal = () => {
    setPasswordReset(defaultPasswordResetValues);
  };

  return (
    <Box
      component="main"
      sx={{
        backgroundImage: `url(${LocalImage.contactBg})`,
        backgroundRepeat: "no-repeat",
        backgroundColor: `${Colors.white}`,
        backgroundSize: "20% auto",
        backgroundPosition: "right bottom",
        flex: 1,
        overflow: "auto",
        px: 3,
        pt: 15,
        pb: 8,
      }}
    >
      <Header
        title={"Users Overview"}
        subTitle="You doing good, Keep it up!"
        btnName="New User"
        onButtonClick={() => openAddUserModal("add")}
        onInputChange={handleSearch}
      />

      <Box sx={{ width: "100%" }} className="table-top-border">
        <Grid container sx={{ pb: 1 }}>
          <Grid item sm={12}>
            <Stack spacing={2} direction="row">
              <Button
                variant="outlined"
                className={`button-style01 ${
                  selectedTab === "all" ? "button-style01-active" : ""
                }`}
                onClick={() => onClickTab("all")}
              >
                All Users
              </Button>
              <Button
                variant="outlined"
                className={`button-style01 ${
                  selectedTab === "active" ? "button-style01-active" : ""
                }`}
                onClick={() => onClickTab("active")}
              >
                Active
              </Button>
              <Button
                variant="outlined"
                className={`button-style01 ${
                  selectedTab === "inactive" ? "button-style01-active" : ""
                }`}
                onClick={() => onClickTab("inactive")}
              >
                Inactive
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>


      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2, boxShadow: "none" }}>

          
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={dense ? "small" : "medium"}
            >
              <EnhancedTableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={rows?.length || 0}
              />

              <TableBody>
                {visibleRows && visibleRows?.length > 0
                  ? visibleRows?.map((row, index) => {
                      const labelId = `enhanced-table-checkbox-${index}`;

                      return (
                        <TableRow
                          hover
                          tabIndex={-1}
                          key={row.user_id}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell
                            component="th"
                            id={labelId}
                            scope="row"
                            align="left"
                          >
                            {row.user_id}
                          </TableCell>
                          <TableCell align="left">{row.username}</TableCell>
                          <TableCell align="left">{row.email}</TableCell>
                          <TableCell align="left">
                            {formatAsUsPhoneNo(row.phone as number)}
                          </TableCell>
                          <TableCell align="left">{row.first_name}</TableCell>
                          <TableCell align="left">{row.last_name}</TableCell>
                          <TableCell align="left">
                            {row.is_active === 1 ? (
                              <CheckIcon />
                            ) : (
                              <CloseIcon />
                            )}
                          </TableCell>
                          <TableCell align="left">
                            {firstLetterCaps(row.role_name as string)}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ color: "orange" }}
                            onClick={() =>
                              openAddUserModal("edit", row as Data)
                            }
                          >
                            Edit{" "}
                            <ArrowDropDownOutlinedIcon
                              sx={{ position: "relative", top: 7 }}
                            />
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ color: "red" }}
                            onClick={() =>
                              deleteUserById(row.user_id as string)
                            }
                          >
                            Delete{" "}
                            <DeleteOutlineIcon
                              sx={{ position: "relative", top: 7 }}
                            />
                          </TableCell>
                          <TableCell
                            align="left"
                            onClick={() =>
                              handleResetPassword(row.user_id as number)
                            }
                          >
                            Reset{" "}
                            <LockResetIcon
                              sx={{ position: "relative", top: 7 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  : null}
              </TableBody>

            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mt: 3,
            }}
          >
            <Typography
              variant="body1"
              component={"body"}
              color={"#717579"}
              fontSize={"10px"}
            >
              Shwoing {getCurrentItemsCount()} from {rows?.length} entries
            </Typography>

            {paginationOrder > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                <Box
                  onClick={() => {
                    if (pageNo > 1) {
                      changePageNo(pageNo - 1);
                    }
                  }}
                  sx={{
                    backgroundColor: "#007CA4",
                    padding: "8px 8px 0px 8px",
                    color: "#fff",
                    borderTopLeftRadius: "6px",
                    borderBottomLeftRadius: "6px",
                  }}
                >
                  <ArrowBackIosIcon sx={{ fontSize: "14px", mt: "2px" }} />
                </Box>
                <Select
                  labelId="demo-select-small-label"
                  id="demo-select-small"
                  value={pageNo}
                  inputProps={{
                    "aria-label": "Without label",
                    sx: { padding: "6px 20px !important" },
                  }}
                  onChange={(event) => {
                    changePageNo(event.target.value as number);
                  }}
                  fullWidth
                  size="small"
                  placeholder="1"
                  IconComponent={() => null}
                >
                  {new Array(paginationOrder)
                    .fill(paginationOrder)
                    .map((item, index) => (
                      <MenuItem value={index + 1} key={index}>
                        {index + 1}
                      </MenuItem>
                    ))}
                </Select>
                <Box
                  onClick={() => {
                    if (pageNo < paginationOrder) {
                      changePageNo(pageNo + 1);
                    }
                  }}
                  sx={{
                    backgroundColor: "#007CA4",
                    padding: "8px 8px 0px 8px",
                    color: "#fff",
                    borderTopRightRadius: "6px",
                    borderBottomRightRadius: "6px",
                  }}
                >
                  <ArrowForwardIosIcon sx={{ fontSize: "14px", mt: "2px" }} />
                </Box>
              </Box>
            ) : null}

          </Box>
        </Paper>
      </Box>

      <Dialog open={addModal} onClose={closeAddUserModal}>
        <Grid container spacing={2} alignItems={"center"}>
          <Grid item md={10}>
            <Box display="flex" justifyContent="center">
              <DialogTitle>
                {`${
                  Object.keys(selectedUser).length === 0 ? "Add" : "Edit"
                } User`}
              </DialogTitle>
            </Box>
          </Grid>
          <Grid item md={2}>
            <Tooltip title="Cancel" style={{ marginRight: 8, float: "right" }}>
              <IconButton onClick={closeAddUserModal}>
                <CancelIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
        <DialogContent>
          <AddUser
            selectedUser={selectedUser}
            selectedUserId={selectedUser?.user_id}
            callback={addNewUser}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={passwordReset.alert.visible || passwordReset.success.visible}
        onClose={closePasswordResetModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {passwordReset.success.visible
            ? passwordReset.success.title
            : passwordReset.alert.title}
        </DialogTitle>
        
        {passwordReset.success.visible ? (
          <DialogContent sx={{ textAlign: "center" }}>
            <Typography>{passwordReset.success.desc}</Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ mt: "8px", fontWeight: "600", mr: "8px" }}>
                {passwordReset.success.password}
              </Typography>
              <div className="tooltip">
                <ContentCopyIcon
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      passwordReset.success.password
                    );
                    handleSnackbar(true, "Copied Successfully!", "success");
                  }}
                />
                <span className="tooltiptext">Click to copy</span>
              </div>
            </Box>
          </DialogContent>
        ) : null}
        <DialogActions>
          {passwordReset.success.visible ? (
            <>
              <Button onClick={closePasswordResetModal}>Close</Button>
            </>
          ) : (
            <>
              <Button autoFocus onClick={() => resetUserPassword()}>
                Yes
              </Button>
              <Button onClick={closePasswordResetModal}>Cancel</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={isLoading} onClose={closeAddUserModal}>
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConfirmAlert.visible}
        onClose={() => handleDeleteConfirmAlert(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Do you want to delete this user?"}
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => handleDeleteConfirmAlert(false)}>No</Button>
          <Button
            onClick={() => proceedToDelete(deleteConfirmAlert.id)}
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      {/* <Dialog
        open={resetPass}
        onClose={handleClose}
        aria-labelledby="draggable-dialog-title"
      >        
        <DialogContent>
          <DialogContentText>
            Are you sure to reset the password of this user?
          </DialogContentText>
        </DialogContent>
        <Box sx={{textAlign: "center", display:"flex",justifyContent:"center"}}>
          <Button        
          variant="contained"
          sx={{ mt: 3, mb: 3, mr:1 }}
          onClick={handleClickOpen01}>Yes</Button>
          <Button        
          variant="contained"
          sx={{ mt: 3, mb: 3 }}
          onClick={handleClose}>Cancel</Button>
        </Box>
      </Dialog>

      <Dialog
        open={resetNewPass}
        onClose={handleClose}
        aria-labelledby="draggable-dialog-title"
      >        
        <DialogContent>
          <DialogContentText>
            <p>Password for this user has been reset successfully!!</p>
            <p>You can copy the new password given below and share it with the user.</p>
          </DialogContentText>
          <DialogContentText sx={{textAlign:"center", fontWeight: "bold"}}>
              {newPassword}
           </DialogContentText>
        </DialogContent>
        <Box sx={{textAlign: "center", display:"flex",justifyContent:"center"}}>
          <Button        
          variant="contained"
          sx={{ mt: 3, mb: 3 }}
          onClick={handleClose}>Close</Button>
        </Box>
      </Dialog> */}
    </Box>
  );
}
